import api from './api';
import { downloadGridExport } from '../utils/gridExport';

const BASE_URL = '/api/rem';

// Helper for file uploads
const getMultipartConfig = () => {
    return {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    };
};

// Every REM list endpoint, as an absolute URL. The server-side grids bind a
// Syncfusion DataManager straight to these (see RemGenericList), so a screen
// needs the URL, not a fetch function.
export const remUrl = (endpoint) => `${BASE_URL}/${endpoint}/`;

/**
 * Download a REM list as .xlsx from the SERVER, over the same filters.
 *
 * The grids page server-side and hold one page, so Syncfusion's own
 * `excelExport()` would write out that page and label it the whole list. Every
 * REM list ViewSet carries `GridExportMixin`, which streams the entire filtered
 * set from `<endpoint>export/`.
 */
// The generic implementation now lives in `utils/gridExport` — every
// server-paged grid needs it, not just REM. Kept under this name so the REM
// callers read the same as they always did.
export const downloadRemExport = (endpoint, params = {}, filename = 'export') =>
    downloadGridExport(endpoint, params, filename);

// Generic CRUD factory
const createCrud = (endpoint) => ({
    url: remUrl(endpoint),
    getList: (params) => api.get(`${BASE_URL}/${endpoint}/`, { params }),
    get: (id) => api.get(`${BASE_URL}/${endpoint}/${id}/`),
    create: (data) => api.post(`${BASE_URL}/${endpoint}/`, data),
    update: (id, data) => api.patch(`${BASE_URL}/${endpoint}/${id}/`, data),
    delete: (id) => api.delete(`${BASE_URL}/${endpoint}/${id}/`),
});

export const remService = {
    // Property Master
    propertyMaster: createCrud('properties'),
    getProperties: (params) => api.get(`${BASE_URL}/properties/`, { params }),
    getProperty: (id) => api.get(`${BASE_URL}/properties/${id}/`),
    createProperty: (data) => api.post(`${BASE_URL}/properties/`, data),
    updateProperty: (id, data) => api.patch(`${BASE_URL}/properties/${id}/`, data),
    deleteProperty: (id) => api.delete(`${BASE_URL}/properties/${id}/`),

    // Reference Data
    getPropertyTypes: () => api.get(`${BASE_URL}/property-types/`),
    getPropertyStatuses: () => api.get(`${BASE_URL}/property-statuses/`),

    // SF Numbers
    getPropertySFNumbers: (propertyId) => api.get(`${BASE_URL}/properties/${propertyId}/sf-numbers/`),
    createPropertySFNumber: (propertyId, data) => api.post(`${BASE_URL}/properties/${propertyId}/sf-numbers/`, data),

    // Ownerships
    getPropertyOwnerships: (propertyId) => api.get(`${BASE_URL}/properties/${propertyId}/ownerships/`),
    createPropertyOwnership: (propertyId, data) => api.post(`${BASE_URL}/properties/${propertyId}/ownerships/`, data),

    // Documents
    getPropertyDocuments: (propertyId) => api.get(`${BASE_URL}/properties/${propertyId}/documents/`),
    uploadPropertyDocument: (propertyId, formData) => api.post(`${BASE_URL}/properties/${propertyId}/documents/`, formData, getMultipartConfig()),

    // Dashboard Stats
    getDashboardStats: (params) => api.get(`${BASE_URL}/dashboard/stats/`, { params }),

    // Reports — derived, server-paged (apps/REM/reports.py)
    reports: {
        rentRoll: createCrud('reports/rent-roll'),
        leaseExpiry: createCrud('reports/lease-expiry'),
        rentArrears: createCrud('reports/rent-arrears'),
        occupancy: createCrud('reports/occupancy'),
        propertyIncomeExpense: createCrud('reports/property-income-expense'),
        rentArrearsSummary: (params) =>
            api.get(`${BASE_URL}/reports/rent-arrears/summary/`, { params }),
        occupancySummary: (params) =>
            api.get(`${BASE_URL}/reports/occupancy/summary/`, { params }),
        tenantLedger: (params) =>
            api.get(`${BASE_URL}/reports/tenant-ledger/`, { params }),
    },

    // Property Valuations
    propertyValuations: createCrud('property-valuations'),

    // Floors
    floors: createCrud('floors'),

    // Spaces
    spaces: createCrud('spaces'),

    // Space Types
    spaceTypes: createCrud('space-types'),

    // Common Areas
    commonAreas: createCrud('common-areas'),

    // Property Tax
    propertyTaxes: createCrud('property-taxes'),

    // Property Insurance
    propertyInsurances: createCrud('property-insurances'),

    // Property Expenses
    propertyExpenses: createCrud('property-expenses'),

    // Tenant Types
    tenantTypes: createCrud('tenant-types'),

    // Tenants
    tenants: createCrud('tenants'),

    // Tenant Contacts
    tenantContacts: createCrud('tenant-contacts'),

    // Tenant Screening
    tenantScreenings: createCrud('tenant-screenings'),

    // Lease Types
    leaseTypes: createCrud('lease-types'),

    // Leases
    leases: createCrud('leases'),

    // Rent Payments
    rentPayments: createCrud('rent-payments'),

    // Utility Charges
    utilityCharges: createCrud('utility-charges'),

    // Tenant Incidents
    tenantIncidents: createCrud('tenant-incidents'),

    // Maintenance Requests
    maintenanceRequests: createCrud('maintenance-requests'),

    // Inspection Schedules
    inspectionSchedules: createCrud('inspection-schedules'),

    // Inspection Reports
    inspectionReports: createCrud('inspection-reports'),

    // Inspectors (active users, for inspector dropdowns)
    inspectors: createCrud('inspectors'),

    // Lease Renewals (legacy - kept for backward compat)
    leaseRenewals: createCrud('lease-renewals'),

    // Lease Renewal Offers
    leaseRenewalOffers: createCrud('lease-renewal-offers'),

    // Lease Terminations
    leaseTerminations: createCrud('lease-terminations'),

    // Space Amenities
    spaceAmenities: createCrud('space-amenities'),

    // Space Modifications
    spaceModifications: createCrud('space-modifications'),

    // Space Occupancies
    spaceOccupancies: createCrud('space-occupancies'),

    // Tenant Documents
    tenantDocuments: createCrud('tenant-documents'),

    // Tenant Histories
    tenantHistories: createCrud('tenant-histories'),

    // Tenant Notifications
    tenantNotifications: createCrud('tenant-notifications'),

    // Tenant Approvals
    tenantApprovals: createCrud('tenant-approvals'),

    // Tenant Addresses
    tenantAddresses: createCrud('tenant-addresses'),

    // Tenant Communications
    tenantCommunications: createCrud('tenant-communications'),

    // Property SF Numbers (flat list)
    propertySFNumbers: createCrud('sf-numbers'),

    // Property Ownerships (flat list)
    propertyOwnerships: createCrud('ownerships'),

    // Property Documents (flat list)
    propertyDocuments: createCrud('documents'),

    // Shared Property Documents
    sharedPropertyDocuments: createCrud('shared-documents'),

    // Maintenance Activities
    maintenanceActivities: createCrud('maintenance-activities'),

    // Module Settings
    moduleSettings: createCrud('module-settings'),

    // Property Statuses (flat list / CRUD)
    propertyStatuses: createCrud('property-statuses'),

    // Property Types (flat list / CRUD)
    propertyTypes: createCrud('property-types'),
};
